import axios from 'axios';

const TASK_SERVICE = process.env.REACT_APP_TASK_SERVICE_URL || 'http://localhost:8008';
export const TASK_SERVICE_URL = TASK_SERVICE;
const TOOL_SERVICE = process.env.REACT_APP_TOOL_SERVICE_URL || 'http://localhost:8002';
const MEMORY_SERVICE = process.env.REACT_APP_MEMORY_SERVICE_URL || 'http://localhost:8006';
const ARTIFACT_SERVICE = process.env.REACT_APP_ARTIFACT_SERVICE_URL || 'http://localhost:8009';
const APPROVAL_SERVICE = process.env.REACT_APP_APPROVAL_SERVICE_URL || 'http://localhost:8010';
const PLANNER_SERVICE = process.env.REACT_APP_PLANNER_SERVICE_URL || 'http://localhost:8004';
const KB_SERVICE = process.env.REACT_APP_KB_SERVICE_URL || '/api/kb-service';
const TOKEN_SERVICE = process.env.REACT_APP_TOKEN_SERVICE_URL || '/api/token-service';
const ADMIN_SERVICE = process.env.REACT_APP_ADMIN_SERVICE_URL || '/api/admin-service';
const SECRETS_SERVICE = process.env.REACT_APP_SECRETS_SERVICE_URL || '/api/secrets-service';
const CONNECTOR_SERVICE = process.env.REACT_APP_CONNECTOR_SERVICE_URL || '/api/connector-service';

const api = axios.create({ timeout: 30000 });
const SESSION_KEY = 'praxys_session_v1';

const getSessionUserId = (): string => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    const userId = parsed?.user?.email;
    return typeof userId === 'string' ? userId : '';
  } catch {
    return '';
  }
};

api.interceptors.request.use((config) => {
  const userId = getSessionUserId();
  if (!userId) return config;
  config.headers = (config.headers || {}) as any;
  (config.headers as any)['X-User-Id'] = userId;
  return config;
});

const _contentDispositionFilename = (header?: string): string => {
  if (!header) return '';
  const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const quotedMatch = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];
  const unquotedMatch = header.match(/filename\s*=\s*([^;\s]+)/i);
  return unquotedMatch?.[1] || '';
};

const _downloadBlob = async (url: string, fallbackFilename: string) => {
  const response = await api.get(url, { responseType: 'blob' });
  const blob = response.data as Blob;
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = _contentDispositionFilename(response.headers?.['content-disposition']) || fallbackFilename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

// ── Tasks ────────────────────────────────────────────────────────────────────

export const createTask = (
  goal: string,
  agentType = 'autonomous',
  agentTemplateId = '',
  userId = '',
  workspaceId = '',
  inputContext: Record<string, any> = {},
  knowledgeBaseIds: string[] = [],
  projectId = '',
  promptMode = 'standard',
) =>
  api.post(`${TASK_SERVICE}/tasks`, {
    goal,
    agent_type: agentType,
    agent_template_id: agentTemplateId,
    user_id: userId,
    workspace_id: workspaceId,
    project_id: projectId,
    input_context: inputContext,
    knowledge_base_ids: knowledgeBaseIds,
    prompt_mode: promptMode,
  });

/** Auto-execute: create + plan + (auto-approve if low-risk) + start in one call. */
export const executeTask = (
  goal: string,
  agentType = 'autonomous',
  agentTemplateId = '',
  userId = '',
  workspaceId = '',
  inputContext: Record<string, any> = {},
  knowledgeBaseIds: string[] = [],
  projectId = '',
  promptMode = 'standard',
) =>
  api.post(`${TASK_SERVICE}/tasks/execute`, {
    goal,
    agent_type: agentType,
    agent_template_id: agentTemplateId,
    user_id: userId,
    workspace_id: workspaceId,
    project_id: projectId,
    input_context: inputContext,
    knowledge_base_ids: knowledgeBaseIds,
    prompt_mode: promptMode,
  });

export const listTasks = (params: any = {}) =>
  api.get(`${TASK_SERVICE}/tasks`, { params });

export const getTask = (taskId: string) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}`);

export const generatePlan = (taskId: string, forceRegenerate = false) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/plan`, { force_regenerate: forceRegenerate });

export const getTaskPlan = (taskId: string) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/plan`);

export const startTask = (taskId: string) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/start`);

export const pauseTask = (taskId: string) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/pause`);

export const resumeTask = (taskId: string) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/resume`);

export const cancelTask = (taskId: string) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/cancel`);

export const deleteTask = (taskId: string) =>
  api.delete(`${TASK_SERVICE}/tasks/${taskId}`);

export const archiveTask = (taskId: string) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/archive`);

export const restoreTask = (taskId: string) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/restore`);

export const getTaskSteps = (taskId: string) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/steps`);

export const getTaskLogs = (taskId: string, limit = 200) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/logs`, { params: { limit } });

export const getTaskArtifacts = (taskId: string) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/artifacts`);

export const shareTask = (
  taskId: string,
  payload: { shared_with_user_id: string; permission?: string },
) => api.post(`${TASK_SERVICE}/tasks/${taskId}/share`, payload);

export const getTaskShares = (taskId: string) =>
  api.get<{ shares: any[] }>(`${TASK_SERVICE}/tasks/${taskId}/shares`);

export const deleteTaskShare = (taskId: string, shareId: string) =>
  api.delete(`${TASK_SERVICE}/tasks/${taskId}/shares/${shareId}`);

export const getSharedLibraryTasks = () =>
  api.get<{ tasks: any[] }>(`${TASK_SERVICE}/tasks/shared-with-me`);

export interface CreateTaskFollowUpOptions {
  inputContext?: Record<string, any>;
  knowledgeBaseIds?: string[];
  promptMode?: string;
  agentType?: string;
  agentTemplateId?: string;
}

export const createTaskFollowUp = (
  taskId: string,
  message: string,
  options: CreateTaskFollowUpOptions = {},
) =>
  api.post(`${TASK_SERVICE}/tasks/${taskId}/follow-ups`, {
    message,
    input_context: options.inputContext || {},
    knowledge_base_ids: options.knowledgeBaseIds || [],
    prompt_mode: options.promptMode || '',
    agent_type: options.agentType || '',
    agent_template_id: options.agentTemplateId || '',
  });

export const getTaskFollowUps = (taskId: string, params: { limit?: number; offset?: number } = {}) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/follow-ups`, { params });

export const getTaskSubtasks = (taskId: string, params: { limit?: number; offset?: number } = {}) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/subtasks`, { params });

export const getTaskLibrary = (taskId: string, params: { limit?: number; offset?: number } = {}) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/library`, { params });

export const listLibraryItems = (scope: 'mine' | 'shared' = 'mine', params: { limit?: number; offset?: number } = {}) =>
  api.get<{ library_items: any[] }>(`${TASK_SERVICE}/library-items`, { params: { scope, ...params } });

export const createLibraryItem = (data: {
  source_type?: string;
  source_task_id?: string;
  source_chat_id?: string;
  source_execution_id?: string;
  title: string;
  summary?: string;
  content_markdown?: string;
  content_html?: string;
  content_text?: string;
  artifact_refs?: any[];
  tags?: string[];
  visibility?: string;
  metadata?: Record<string, any>;
}) => api.post(`${TASK_SERVICE}/library-items`, data);

export const deleteLibraryItem = (itemId: string) =>
  api.delete(`${TASK_SERVICE}/library-items/${itemId}`);

export const getLibraryItemShares = (itemId: string) =>
  api.get<{ shares: any[] }>(`${TASK_SERVICE}/library-items/${itemId}/shares`);

export const shareLibraryItem = (
  itemId: string,
  payload: { shared_with_user_id: string; permission?: string },
) => api.post(`${TASK_SERVICE}/library-items/${itemId}/shares`, payload);

export const deleteLibraryItemShare = (itemId: string, shareId: string) =>
  api.delete(`${TASK_SERVICE}/library-items/${itemId}/shares/${shareId}`);

export const convertTaskToProject = (
  taskId: string,
  data: { name?: string; description?: string } = {},
) => api.post(`${TASK_SERVICE}/tasks/${taskId}/convert-to-project`, data);

/** All persisted activity events — polling fallback when SSE is unavailable. */
export const getTaskActivity = (taskId: string, limit = 500) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/activity`, { params: { limit } });

/** All tool call logs for a task. */
export const getTaskToolCalls = (taskId: string) =>
  api.get(`${TASK_SERVICE}/tasks/${taskId}/tool-calls`);

// ── Plans ────────────────────────────────────────────────────────────────────

export const approvePlan = (planId: string, approvedBy = 'user') =>
  api.post(`${TASK_SERVICE}/plans/${planId}/approve`, { approved_by: approvedBy });

export const rejectPlan = (planId: string, reason = '') =>
  api.post(`${TASK_SERVICE}/plans/${planId}/reject`, { reason });

export const updatePlan = (planId: string, planJson: string) =>
  api.put(`${TASK_SERVICE}/plans/${planId}`, { plan_json: planJson });

// ── Agent templates (task-service) ───────────────────────────────────────────

export const listAgentTemplates = () => api.get(`${TASK_SERVICE}/agents`);

export const createAgentTemplate = (data: any) =>
  api.post(`${TASK_SERVICE}/agents`, data);

export const updateAgentTemplate = (agentId: string, data: any) =>
  api.put(`${TASK_SERVICE}/agents/${agentId}`, data);

// ── Tools ────────────────────────────────────────────────────────────────────

export const listToolsService = () => api.get(`${TOOL_SERVICE}/tools`);

export const executeTool = (toolName: string, params: any) =>
  api.post(`${TOOL_SERVICE}/tools/${toolName}/execute`, params);

// ── Approvals ────────────────────────────────────────────────────────────────

export const listApprovals = (params: any = {}) =>
  api.get(`${APPROVAL_SERVICE}/approvals`, { params });

export const approveRequest = (approvalId: string, note = '', resolvedBy = 'user') =>
  api.post(`${APPROVAL_SERVICE}/approvals/${approvalId}/approve`, {
    note,
    resolved_by: resolvedBy,
  });

export const rejectRequest = (approvalId: string, note = '', resolvedBy = 'user') =>
  api.post(`${APPROVAL_SERVICE}/approvals/${approvalId}/reject`, {
    note,
    resolved_by: resolvedBy,
  });

// ── Memory ───────────────────────────────────────────────────────────────────

export const searchMemory = (query: string, namespace?: string, limit = 20) =>
  api.get(`${MEMORY_SERVICE}/memory/search`, { params: { query, namespace, limit } });

export const listMemory = (params: any = {}) =>
  api.get(`${MEMORY_SERVICE}/memory`, { params });

export const listMemoryByTask = (taskId: string) =>
  api.get(`${MEMORY_SERVICE}/memory`, { params: { task_id: taskId } });

export const addMemory = (
  text: string,
  options: {
    namespace?: string;
    metadata?: any;
    workspaceId?: string;
    projectId?: string;
    userId?: string;
    taskId?: string;
  } = {},
) => {
  const {
    namespace = 'default',
    metadata = {},
    workspaceId,
    projectId,
    userId,
    taskId,
  } = options;
  const payload: any = {
    text,
    namespace,
    metadata,
  };
  if (workspaceId) payload.workspace_id = workspaceId;
  if (projectId) payload.project_id = projectId;
  if (userId) payload.user_id = userId;
  if (taskId) payload.task_id = taskId;
  return api.post(`${MEMORY_SERVICE}/memory`, payload);
};

export const deleteMemory = (entryId: string) =>
  api.delete(`${MEMORY_SERVICE}/memory/${entryId}`);

// ── Artifacts ────────────────────────────────────────────────────────────────

export const downloadArtifactUrl = (artifactId: string) =>
  `${ARTIFACT_SERVICE}/artifacts/${artifactId}/download`;

export const downloadArtifact = (artifactId: string, fallbackFilename: string) =>
  _downloadBlob(`${ARTIFACT_SERVICE}/artifacts/${artifactId}/download`, fallbackFilename);

export const listArtifacts = (params: { task_id?: string; limit?: number } = {}) =>
  api.get(`${ARTIFACT_SERVICE}/artifacts`, { params });

/**
 * Upload a file as task input context (before the task is created).
 * Returns `{ artifact_id, filename, size_bytes, content_type }`.
 */
export const uploadContextFile = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`${ARTIFACT_SERVICE}/artifacts/context-upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
};

export const extractTextFromUploadedFile = (file: File, maxChars = 24000) => {
  const form = new FormData();
  form.append('file', file);
  form.append('max_chars', String(maxChars));
  return api.post(`${ARTIFACT_SERVICE}/artifacts/extract-text`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
};

export const downloadTaskExport = (
  taskId: string,
  format: 'docx' | 'pdf',
  fallbackFilename: string,
) => _downloadBlob(`${TASK_SERVICE}/tasks/${taskId}/export/${format}`, fallbackFilename);

// ── Chat (direct LLM — no task created) ──────────────────────────────────────

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Optional display-only text (e.g. hides extracted file content from the chat bubble). */
  displayContent?: string;
}

export interface ChatRequestOptions {
  sessionId?: string;
  resetSession?: boolean;
  systemPrompt?: string;
}

/**
 * Send a conversation to the LLM and get a direct reply.
 * Routes through the task-orchestration-service ``/chat`` endpoint, which is
 * already proxied by nginx at ``/api/task-service/``.  No separate
 * planner-service process is required.
 */
export const chatWithLLM = (messages: ChatMessage[], options: ChatRequestOptions = {}) =>
  api.post(
    `${TASK_SERVICE}/chat`,
    {
      messages,
      session_id: options.sessionId,
      reset_session: !!options.resetSession,
      system_prompt: options.systemPrompt,
    },
    { timeout: 60000 },
  );

export interface HelpAskRequest {
  question: string;
  view: string;
  page_title: string;
  summary: string;
  common_tasks: string[];
  headings: string[];
  actions: string[];
  history: Pick<ChatMessage, 'role' | 'content'>[];
}

export const askHelp = (request: HelpAskRequest) =>
  api.post<{ reply: string }>(`${TASK_SERVICE}/help/ask`, request, { timeout: 60000 });

// ── SSE ──────────────────────────────────────────────────────────────────────

/** Open an SSE stream — replays history then goes live. */
export const streamTaskEvents = (taskId: string): EventSource =>
  new EventSource(`${TASK_SERVICE}/tasks/${taskId}/events?user_id=${encodeURIComponent(getSessionUserId())}`);

// ── Desktop / Browser Workspace ───────────────────────────────────────────────

const normalizeDesktopServiceUrl = (value?: string): string => {
  const raw = (value || '/api/task-service').replace(/\/+$/, '');
  // Older builds pointed the browser workspace at playbook-service through this
  // alias. Browser sessions now live in the task orchestration service.
  return raw === '/api/desktop-service' ? '/api/task-service' : raw;
};

export const DESKTOP_SERVICE = normalizeDesktopServiceUrl(
  process.env.REACT_APP_DESKTOP_SERVICE_URL,
);

export const listDesktopSessions = () =>
  api.get(`${DESKTOP_SERVICE}/desktop/sessions`);

export const getDesktopSession = (sessionId: string) =>
  api.get(`${DESKTOP_SERVICE}/desktop/sessions/${sessionId}`);

export const createDesktopSession = (body: {
  session_id?: string;
  task_id?: string;
  step_id?: string;
  workspace_id?: string;
  url?: string;
  headless?: boolean;
}) => api.post(`${DESKTOP_SERVICE}/desktop/sessions`, body);

export const deleteDesktopSession = (sessionId: string) =>
  api.delete(`${DESKTOP_SERVICE}/desktop/sessions/${sessionId}`);

export const getLatestScreenshot = (sessionId: string) =>
  api.get(`${DESKTOP_SERVICE}/desktop/sessions/${sessionId}/screenshot/latest`);

export const listScreenshots = (sessionId: string) =>
  api.get(`${DESKTOP_SERVICE}/desktop/sessions/${sessionId}/screenshots`);

// ── Knowledge Bases ───────────────────────────────────────────────────────────

export const listKnowledgeBases = (params: any = {}) =>
  api.get(`${KB_SERVICE}/knowledge-bases`, { params });

export const searchKnowledgeBaseCatalog = (query: string, limit = 20) =>
  api.get(`${KB_SERVICE}/kb/search`, { params: { q: query, limit } });

export const createKnowledgeBase = (data: {
  name: string;
  description?: string;
  workspace_id?: string;
  project_id?: string;
  user_id?: string;
  chunking_strategy?: string;
  embedding_provider?: string;
  embedding_model?: string;
}) => api.post(`${KB_SERVICE}/knowledge-bases`, data);

export const createKnowledgeBaseAlias = (data: {
  name: string;
  description?: string;
  visibility?: string;
  tags?: string[];
  workspace_id?: string;
  project_id?: string;
  user_id?: string;
}) => api.post(`${KB_SERVICE}/kb`, data);

export const getKnowledgeBase = (kbId: string) =>
  api.get(`${KB_SERVICE}/knowledge-bases/${kbId}`);

export const updateKnowledgeBase = (kbId: string, data: any) =>
  api.put(`${KB_SERVICE}/knowledge-bases/${kbId}`, data);

export const deleteKnowledgeBase = (kbId: string) =>
  api.delete(`${KB_SERVICE}/knowledge-bases/${kbId}`);

export const restoreKnowledgeBase = (kbId: string, data?: { name?: string }) =>
  api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/restore`, data || {});

export const deleteKnowledgeBasePermanently = (kbId: string) =>
  api.delete(`${KB_SERVICE}/knowledge-bases/${kbId}/permanent`);

export interface KnowledgeBaseShare {
  id: string;
  knowledge_base_id: string;
  shared_with_user_id: string;
  shared_by_user_id?: string;
  permission: string;
  created_at: string;
}

export const listKnowledgeBaseShares = (kbId: string) =>
  api.get<{ shares: KnowledgeBaseShare[] }>(`${KB_SERVICE}/knowledge-bases/${kbId}/shares`);

export const shareKnowledgeBase = (
  kbId: string,
  payload: { shared_with_user_id: string; permission?: string },
) => api.post<{ share: KnowledgeBaseShare }>(`${KB_SERVICE}/knowledge-bases/${kbId}/shares`, payload);

export const deleteKnowledgeBaseShare = (kbId: string, sharedUserId: string) =>
  api.delete(`${KB_SERVICE}/knowledge-bases/${kbId}/shares/${encodeURIComponent(sharedUserId)}`);

export const importKnowledgeText = (
  kbId: string,
  data: { title: string; text: string; metadata?: Record<string, any> },
) => api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/documents/text`, data);

export const importKnowledgeUrl = (
  kbId: string,
  data: { url: string; title?: string; metadata?: Record<string, any> },
) => api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/documents/url`, data);

export const uploadKnowledgeDocument = (
  kbId: string,
  file: File,
  title?: string,
  metadata?: Record<string, any>,
) => {
  const form = new FormData();
  form.append('file', file);
  if (title) form.append('title', title);
  if (metadata) form.append('metadata', JSON.stringify(metadata));
  return api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/documents/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
};

export const importKnowledgeArtifact = (
  kbId: string,
  data: { artifact_id: string; title?: string; metadata?: Record<string, any> },
) => api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/documents/artifact`, data);

export const listKnowledgeDocuments = (kbId: string, params: any = {}) =>
  api.get(`${KB_SERVICE}/knowledge-bases/${kbId}/documents`, { params });

export const getKnowledgeDocument = (kbId: string, documentId: string) =>
  api.get(`${KB_SERVICE}/knowledge-bases/${kbId}/documents/${documentId}`);

export const deleteKnowledgeDocument = (kbId: string, documentId: string) =>
  api.delete(`${KB_SERVICE}/knowledge-bases/${kbId}/documents/${documentId}`);

export const listKnowledgeChunks = (kbId: string, params: any = {}) =>
  api.get(`${KB_SERVICE}/knowledge-bases/${kbId}/chunks`, { params });

export const deleteKnowledgeChunk = (kbId: string, chunkId: string) =>
  api.delete(`${KB_SERVICE}/knowledge-bases/${kbId}/chunks/${chunkId}`);

export const searchKnowledgeBase = (kbId: string, query: string, limit = 10) =>
  api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/search`, { query, limit });

export const searchKnowledgeBases = (
  knowledgeBaseIds: string[],
  query: string,
  limit = 10,
) =>
  api.post(`${KB_SERVICE}/knowledge-bases/search`, {
    knowledge_base_ids: knowledgeBaseIds,
    query,
    limit,
  });

export const semanticSearchKnowledgeBases = (
  knowledgeBaseIds: string[],
  query: string,
  limit = 10,
) =>
  api.post(`${KB_SERVICE}/knowledge-bases/semantic-search`, {
    knowledge_base_ids: knowledgeBaseIds,
    query,
    limit,
  });

const KB_OVERVIEW_PATTERNS = [
  /\bwhat('?s|\s+is)\s+(in\s+)?(this|the|my)?\s*(kb|knowledge\s*base)\b/i,
  /\bwhat('?s|\s+is)\s+(this|the|my)?\s*(kb|knowledge\s*base)\s+(is\s+)?about\b/i,
  /\b(summarize|summarise|describe|explain|overview\s+of|tell\s+me\s+about)\s+(this|the|my)?\s*(kb|knowledge\s*base)\b/i,
  /\btell\s+me\s+what('?s|\s+is)?\s+(this|the|my)?\s*(kb|knowledge\s*base)\s+(is\s+)?about\b/i,
  /\bwhat\s+(does|is)\s+(this|the|my)?\s*(kb|knowledge\s*base)\s+(contain|about)\b/i,
  /\bwhat\s+documents?\s+(are\s+)?(in|inside)\s+(this|the|my)?\s*(kb|knowledge\s*base)\b/i,
  /^\s*semantic\s+search\s+(in|of|for)\s+(kb|knowledge\s*base)\s*$/i,
];

export const isKnowledgeBaseOverviewQuery = (query: string): boolean =>
  KB_OVERVIEW_PATTERNS.some(pattern => pattern.test((query || '').trim()));

export async function buildSelectedKnowledgeBasePromptContext(
  knowledgeBaseIds: string[],
  query: string,
  limit = 10,
): Promise<string> {
  if (!knowledgeBaseIds.length) return '';

  const overview = isKnowledgeBaseOverviewQuery(query);
  const kbRes = await semanticSearchKnowledgeBases(
    knowledgeBaseIds,
    overview ? 'overview of this knowledge base contents documents topics themes' : query,
    overview ? Math.max(limit, 16) : limit,
  );
  const results: any[] = kbRes.data?.results ?? kbRes.data?.chunks ?? [];
  if (!results.length) return '';

  const kbNames = Array.from(new Set(results.map((r: any) => r.knowledge_base_name).filter(Boolean)));
  const snippets = results
    .map((r: any, i: number) => {
      const kbLabel = r.knowledge_base_name ? `KB: ${r.knowledge_base_name}\n` : '';
      return `[${i + 1}] ${kbLabel}Document: ${r.document_title ?? 'Document'}\n${r.text ?? r.chunk_text ?? ''}`;
    })
    .join('\n\n');

  const intent = overview
    ? (
      'The user is asking what the selected Knowledge Base contains. '
      + 'Use the excerpts below to describe the KB in rich detail: its purpose, documents, topics, entities, important facts, patterns, and useful ways to query it. '
      + 'Do not define the product feature named "Semantic Search in KB" unless the KB excerpts themselves are about that feature.'
    )
    : 'Use the excerpts below as the selected Knowledge Base evidence for the user question.';

  return [
    `Selected Knowledge Base Context${kbNames.length ? ` (${kbNames.join(', ')})` : ''}:`,
    intent,
    '',
    snippets,
    '',
    '---',
    '',
    'Answer the user from the selected Knowledge Base context above. If the context is insufficient, say what is missing instead of explaining generic KB/search concepts.',
    '',
  ].join('\n');
}

export const listKnowledgeBaseSuggestedTasks = (kbId: string, params: any = {}) =>
  api.get(`${KB_SERVICE}/knowledge-bases/${kbId}/suggested-tasks`, { params });

export const createKnowledgeBaseEntry = (data: {
  knowledge_base_id: string;
  title: string;
  summary?: string;
  content_markdown?: string;
  content_json?: Record<string, any>;
  tags?: string[];
  source_module?: string;
  source_id?: string;
  metadata?: Record<string, any>;
  artifact_ids?: string[];
}) => api.post(`${KB_SERVICE}/kb/entries`, data);

export const updateKnowledgeBaseEntry = (
  entryId: string,
  data: {
    title?: string;
    summary?: string;
    content_markdown?: string;
    content_json?: Record<string, any>;
    tags?: string[];
    metadata?: Record<string, any>;
    artifact_ids?: string[];
  },
) => api.put(`${KB_SERVICE}/kb/entries/${entryId}`, data);

export const attachKnowledgeBaseEntryArtifacts = (
  entryId: string,
  artifactIds: string[],
) => api.post(`${KB_SERVICE}/kb/entries/${entryId}/attachments`, { artifact_ids: artifactIds });

export const listKnowledgeBaseEntriesBySource = (sourceModule: string, sourceId: string) =>
  api.get<{ entries: any[] }>(`${KB_SERVICE}/kb/entries/by-source`, {
    params: { source_module: sourceModule, source_id: sourceId },
  });

export const createKnowledgeBaseSuggestedTask = (
  kbId: string,
  data: {
    name: string;
    description?: string;
    suggested_prompt?: string;
    recommended_mode?: 'chat' | 'deep_search' | 'autonomous';
    suggested_knowledge_base_ids?: string[];
    tags?: string[];
  },
) => api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/suggested-tasks`, data);

export const updateKnowledgeBaseSuggestedTask = (
  kbId: string,
  suggestedTaskId: string,
  data: {
    name?: string;
    description?: string;
    suggested_prompt?: string;
    recommended_mode?: 'chat' | 'deep_search' | 'autonomous';
    suggested_knowledge_base_ids?: string[];
    tags?: string[];
  },
) => api.put(`${KB_SERVICE}/knowledge-bases/${kbId}/suggested-tasks/${suggestedTaskId}`, data);

export const deleteKnowledgeBaseSuggestedTask = (kbId: string, suggestedTaskId: string) =>
  api.delete(`${KB_SERVICE}/knowledge-bases/${kbId}/suggested-tasks/${suggestedTaskId}`);

export const generateKnowledgeBaseSuggestedTasks = (
  kbId: string,
  data: { count?: number } = {},
) => api.post(`${KB_SERVICE}/knowledge-bases/${kbId}/suggested-tasks/generate`, data);

// ── API Tokens ────────────────────────────────────────────────────────────────

export interface ApiTokenSummary {
  id: string;
  token_prefix: string;
  name: string;
  scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
  revoked: boolean;
  expired: boolean;
  created_at: string;
  token_type: 'dev' | 'prod';
  monthly_limit: number;
  monthly_limit_enabled: boolean;
  pii_restrictions_enabled: boolean;
  usage_count: number;
  updated_at?: string;
}

export interface CreatedApiToken extends ApiTokenSummary {
  token: string; // Full value — only available immediately after creation
}

export const listApiTokens = (userId: string, includeRevoked = true) =>
  api.get<{ tokens: ApiTokenSummary[] }>(`${TOKEN_SERVICE}/api-tokens`, {
    params: { user_id: userId, include_revoked: includeRevoked },
  });

export const createApiToken = (data: {
  user_id: string;
  name: string;
  scopes?: string[];
  expires_at?: string | null;
}) => api.post<CreatedApiToken>(`${TOKEN_SERVICE}/api-tokens`, data);

export const updateApiToken = (
  tokenId: string,
  data: {
    user_id: string;
    name: string;
    token_type?: 'dev' | 'prod';
    monthly_limit?: number;
    monthly_limit_enabled?: boolean;
    pii_restrictions_enabled?: boolean;
  },
) => api.patch<ApiTokenSummary>(`${TOKEN_SERVICE}/api-tokens/${tokenId}`, data);

export const revokeApiToken = (tokenId: string, userId: string) =>
  api.delete(`${TOKEN_SERVICE}/api-tokens/${tokenId}`, {
    params: { user_id: userId },
  });

// ── Admin ─────────────────────────────────────────────────────────────────────

export interface AdminUserStats {
  tasks_total: number;
  tasks_running: number;
  kb_total: number;
  memory_total: number;
  tokens_total: number;
  tokens_active: number;
}

export interface AdminUser {
  id: string;
  user_id: string;
  display_name: string;
  status: 'active' | 'pending' | 'locked' | 'removed';
  notes: string;
  created_at: string;
  updated_at: string;
  stats?: AdminUserStats;
}

export interface BetaInvite {
  id: string;
  email: string;
  display_name: string;
  note: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invited_by: string;
  accepted_by_user_id: string;
  accepted_at: string;
  expires_at: string;
  last_sent_at: string;
  email_sent_count: number;
  last_email_error: string;
  created_at: string;
  updated_at: string;
  invite_link?: string;
}

export interface AdminMetrics {
  users: {
    total_discovered: number;
    active: number;
    pending?: number;
    locked: number;
    removed: number;
  };
  tasks: {
    total: number;
    running: number;
    completed: number;
    failed: number;
  };
  knowledge_bases: {
    total: number;
    documents: number;
    chunks: number;
  };
  agents: {
    total: number;
    executions_total: number;
  };
  memory: { total: number };
  api_tokens: { total: number; active: number };
  generated_at: string;
}

export interface AuditEvent {
  id: string;
  actor_user_id: string;
  event_type: string;
  entity_type: string;
  entity_id: string;
  action: string;
  before_json: string;
  after_json: string;
  metadata_json: string;
  created_at: string;
}

const _adminHeaders = (adminEmail: string) => ({
  headers: { 'X-Admin-User': adminEmail },
});

export const registerUser = (userId: string, displayName: string, inviteToken = '') =>
  api.post(
    `${ADMIN_SERVICE}/admin/users/register`,
    { user_id: userId, display_name: displayName, invite_token: inviteToken },
  );

export const createBetaInvite = (
  adminEmail: string,
  payload: { email: string; display_name?: string; note?: string; expires_at?: string },
) =>
  api.post<{ invite: BetaInvite; email_sent: boolean; email_error: string }>(
    `${ADMIN_SERVICE}/admin/beta-invites`,
    payload,
    _adminHeaders(adminEmail),
  );

export const listBetaInvites = (adminEmail: string, status?: string) =>
  api.get<{ invites: BetaInvite[]; total: number }>(
    `${ADMIN_SERVICE}/admin/beta-invites`,
    { ...(_adminHeaders(adminEmail)), params: status ? { status } : {} },
  );

export const revokeBetaInvite = (adminEmail: string, inviteId: string) =>
  api.post<{ invite: BetaInvite }>(
    `${ADMIN_SERVICE}/admin/beta-invites/${encodeURIComponent(inviteId)}/revoke`,
    {},
    _adminHeaders(adminEmail),
  );

export const resendBetaInvite = (adminEmail: string, inviteId: string) =>
  api.post<{ invite: BetaInvite; email_sent: boolean; email_error: string }>(
    `${ADMIN_SERVICE}/admin/beta-invites/${encodeURIComponent(inviteId)}/resend`,
    {},
    _adminHeaders(adminEmail),
  );

export const listAdminUsers = (adminEmail: string, status?: string) =>
  api.get<{ users: AdminUser[]; total: number }>(
    `${ADMIN_SERVICE}/admin/users`,
    { ...(_adminHeaders(adminEmail)), params: status ? { status } : {} },
  );

export const getAdminUser = (adminEmail: string, userId: string) =>
  api.get<AdminUser>(
    `${ADMIN_SERVICE}/admin/users/${encodeURIComponent(userId)}`,
    _adminHeaders(adminEmail),
  );

export const lockUser = (adminEmail: string, userId: string, reason: string) =>
  api.post(
    `${ADMIN_SERVICE}/admin/users/${encodeURIComponent(userId)}/lock`,
    { reason, actor_user_id: adminEmail },
    _adminHeaders(adminEmail),
  );

export const unlockUser = (adminEmail: string, userId: string) =>
  api.post(
    `${ADMIN_SERVICE}/admin/users/${encodeURIComponent(userId)}/unlock`,
    { actor_user_id: adminEmail },
    _adminHeaders(adminEmail),
  );

export const removeUser = (adminEmail: string, userId: string, reason: string) =>
  api.delete(
    `${ADMIN_SERVICE}/admin/users/${encodeURIComponent(userId)}`,
    {
      ...(_adminHeaders(adminEmail)),
      params: { reason, actor_user_id: adminEmail },
    },
  );

export const restoreUser = (adminEmail: string, userId: string) =>
  api.post(
    `${ADMIN_SERVICE}/admin/users/${encodeURIComponent(userId)}/restore`,
    { actor_user_id: adminEmail },
    _adminHeaders(adminEmail),
  );

export const clearUserData = (adminEmail: string, userId: string, reason: string) =>
  api.post(
    `${ADMIN_SERVICE}/admin/users/${encodeURIComponent(userId)}/clear-data`,
    { reason, actor_user_id: adminEmail },
    _adminHeaders(adminEmail),
  );

/** Returns a URL to trigger browser download of the user's exported data. */
export const exportUserDataUrl = (userId: string, adminEmail: string) =>
  `${ADMIN_SERVICE}/admin/users/${encodeURIComponent(userId)}/export?x_admin_user=${encodeURIComponent(adminEmail)}`;

export const getAdminMetrics = (adminEmail: string) =>
  api.get<AdminMetrics>(`${ADMIN_SERVICE}/admin/metrics`, _adminHeaders(adminEmail));

export const getAdminAuditLog = (adminEmail: string, limit = 100, entityId?: string) =>
  api.get<{ events: AuditEvent[]; total: number }>(
    `${ADMIN_SERVICE}/admin/audit-log`,
    {
      ...(_adminHeaders(adminEmail)),
      params: { limit, ...(entityId ? { entity_id: entityId } : {}) },
    },
  );

// ── Feature Requests ───────────────────────────────────────────────────────────

export type FeatureRequestStatus =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'APPROVED_FOR_VOTING'
  | 'NEEDS_CLARIFICATION'
  | 'UNDER_CONSIDERATION'
  | 'PLANNED'
  | 'IN_DEVELOPMENT'
  | 'RELEASED'
  | 'DECLINED'
  | 'MERGED'
  | 'ARCHIVED';

export type FeatureRequestCategory =
  | 'AI_AGENTS'
  | 'PLAYBOOKS'
  | 'KNOWLEDGE_BASE'
  | 'API'
  | 'INTEGRATIONS'
  | 'DESKTOP_APP'
  | 'UI_UX'
  | 'ADMIN'
  | 'SECURITY'
  | 'BILLING'
  | 'OTHER';

export type FeatureRequestImpactLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type FeatureRequestVisibility = 'PUBLIC' | 'PRIVATE_ENTERPRISE' | 'INTERNAL_ONLY';

export interface FeatureRequestItem {
  id: string;
  title: string;
  description: string;
  problemStatement: string;
  proposedSolution: string;
  category: FeatureRequestCategory;
  status: FeatureRequestStatus;
  visibility: FeatureRequestVisibility;
  impactLevel: FeatureRequestImpactLevel;
  createdByUserId: string;
  createdByName: string;
  approvedByUserId: string;
  approvedOn: string;
  rejectedByUserId: string;
  rejectedOn: string;
  rejectionReason: string;
  voteCount: number;
  commentCount: number;
  priorityScore: number;
  duplicateOfId: string;
  mergedIntoId: string;
  assignedKnowledgeBaseId: string;
  assignedKnowledgeBaseName: string;
  copilotTaskId: string;
  copilotTaskStatus: string;
  copilotTaskCreatedOn: string;
  isPublic: boolean;
  createdOn: string;
  updatedOn: string;
  lastStatusChangedOn: string;
}

export interface FeatureRequestComment {
  id: string;
  featureRequestId: string;
  userId: string;
  userDisplayName: string;
  comment: string;
  isAdminComment: boolean;
  createdOn: string;
  updatedOn: string;
}

export interface FeatureRequestStatusHistory {
  id: string;
  featureRequestId: string;
  oldStatus: string;
  newStatus: string;
  changedByUserId: string;
  changedByName: string;
  note: string;
  createdOn: string;
}

export interface FeatureRequestListResponse {
  items: FeatureRequestItem[];
  total: number;
}

export interface FeatureRequestAnalytics {
  total_pending: number;
  total_approved: number;
  most_voted_requests: Array<{ id: string; title: string; vote_count: number; status: string }>;
  top_categories: Array<{ category: string; count: number }>;
  requests_by_status: Array<{ status: string; count: number }>;
  average_approval_time_hours: number;
  trending_requests: Array<{ id: string; title: string; priority_score: number; vote_count: number; status: string }>;
}

export const getFeatureRequests = (filters: {
  status?: string;
  category?: string;
  sort?: 'mostVoted' | 'newest' | 'trending' | 'recentlyUpdated';
  search?: string;
  mine?: boolean;
  following?: boolean;
} = {}) => api.get<FeatureRequestListResponse>(`${ADMIN_SERVICE}/api/feature-requests`, { params: filters });

export const getFeatureRequestById = (id: string, adminEmail?: string) =>
  api.get<{
    item: FeatureRequestItem;
    currentUserVote: number;
    isFollowing: boolean;
    comments: FeatureRequestComment[];
    statusHistory: FeatureRequestStatusHistory[];
  }>(
    `${ADMIN_SERVICE}/api/feature-requests/${id}`,
    adminEmail ? _adminHeaders(adminEmail) : undefined,
  );

export const createFeatureRequest = (payload: {
  title: string;
  description?: string;
  problemStatement?: string;
  proposedSolution?: string;
  category: FeatureRequestCategory;
  impactLevel: FeatureRequestImpactLevel;
  visibility?: FeatureRequestVisibility;
}) =>
  api.post<{ item: FeatureRequestItem; possibleDuplicates: Array<{ id: string; title: string; similarity_score: number }> }>(
    `${ADMIN_SERVICE}/api/feature-requests`,
    payload,
  );

export const updateFeatureRequest = (id: string, payload: Record<string, any>, adminEmail?: string) =>
  api.put<{ item: FeatureRequestItem }>(
    `${ADMIN_SERVICE}/api/feature-requests/${id}`,
    payload,
    adminEmail ? _adminHeaders(adminEmail) : undefined,
  );

export const voteFeatureRequest = (id: string) =>
  api.post<{ voted: boolean; voteCount: number }>(`${ADMIN_SERVICE}/api/feature-requests/${id}/vote`);

export const removeFeatureRequestVote = (id: string) =>
  api.delete<{ removed: boolean; voteCount: number }>(`${ADMIN_SERVICE}/api/feature-requests/${id}/vote`);

export const getMyFeatureRequests = () =>
  api.get<FeatureRequestListResponse>(`${ADMIN_SERVICE}/api/feature-requests/my`);

export const getFeatureRequestComments = (id: string, adminEmail?: string) =>
  api.get<{ items: FeatureRequestComment[] }>(
    `${ADMIN_SERVICE}/api/feature-requests/${id}/comments`,
    adminEmail ? _adminHeaders(adminEmail) : undefined,
  );

export const addFeatureRequestComment = (id: string, payload: { comment: string }, adminEmail?: string) =>
  api.post<{ item: FeatureRequestComment }>(
    `${ADMIN_SERVICE}/api/feature-requests/${id}/comments`,
    payload,
    adminEmail ? _adminHeaders(adminEmail) : undefined,
  );

export const followFeatureRequest = (id: string) =>
  api.post<{ following: boolean }>(`${ADMIN_SERVICE}/api/feature-requests/${id}/follow`);

export const unfollowFeatureRequest = (id: string) =>
  api.delete<{ following: boolean }>(`${ADMIN_SERVICE}/api/feature-requests/${id}/follow`);

export const getPendingFeatureRequests = (adminEmail: string) =>
  api.get<FeatureRequestListResponse>(`${ADMIN_SERVICE}/api/admin/feature-requests/pending`, _adminHeaders(adminEmail));

export const approveFeatureRequest = (id: string, adminEmail: string) =>
  api.post<{ item: FeatureRequestItem }>(
    `${ADMIN_SERVICE}/api/admin/feature-requests/${id}/approve`,
    {},
    _adminHeaders(adminEmail),
  );

export const rejectFeatureRequest = (id: string, payload: { rejectionReason: string }, adminEmail: string) =>
  api.post<{ item: FeatureRequestItem }>(
    `${ADMIN_SERVICE}/api/admin/feature-requests/${id}/reject`,
    payload,
    _adminHeaders(adminEmail),
  );

export const requestFeatureClarification = (id: string, payload: { note: string }, adminEmail: string) =>
  api.post<{ item: FeatureRequestItem }>(
    `${ADMIN_SERVICE}/api/admin/feature-requests/${id}/needs-clarification`,
    payload,
    _adminHeaders(adminEmail),
  );

export const updateFeatureRequestStatus = (id: string, payload: { status: FeatureRequestStatus; note?: string }, adminEmail: string) =>
  api.post<{ item: FeatureRequestItem }>(
    `${ADMIN_SERVICE}/api/admin/feature-requests/${id}/status`,
    payload,
    _adminHeaders(adminEmail),
  );

export const assignFeatureRequestKnowledgeBase = (id: string, payload: { knowledgeBaseId: string }, adminEmail: string) =>
  api.post<{ item: FeatureRequestItem }>(
    `${ADMIN_SERVICE}/api/admin/feature-requests/${id}/assign-kb`,
    payload,
    _adminHeaders(adminEmail),
  );

export const generateFeatureRequestCopilotTask = (id: string, adminEmail: string) =>
  api.post<{ item: FeatureRequestItem; task: any }>(
    `${ADMIN_SERVICE}/api/admin/feature-requests/${id}/generate-copilot-task`,
    {},
    _adminHeaders(adminEmail),
  );

export const mergeFeatureRequest = (
  id: string,
  payload: { targetFeatureRequestId: string; transferComments?: boolean },
  adminEmail: string,
) =>
  api.post<{
    sourceId: string;
    targetId: string;
    votesTransferred: number;
    commentsTransferred: number;
  }>(
    `${ADMIN_SERVICE}/api/admin/feature-requests/${id}/merge`,
    payload,
    _adminHeaders(adminEmail),
  );

export const getFeatureRequestAnalytics = (adminEmail: string) =>
  api.get<FeatureRequestAnalytics>(`${ADMIN_SERVICE}/api/admin/feature-requests/analytics`, _adminHeaders(adminEmail));

// ── User Secrets ──────────────────────────────────────────────────────────────

export type SecretRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type SecretCategory =
  | 'API_KEY'
  | 'PASSWORD'
  | 'SSH'
  | 'DATABASE'
  | 'CLOUD'
  | 'TOKEN'
  | 'CERTIFICATE'
  | 'OTHER';

export interface UserSecretSummary {
  id: string;
  user_id: string;
  secret_name: string;
  description: string;
  category: SecretCategory;
  risk_level: SecretRiskLevel;
  masked_preview: string;
  is_active: boolean;
  requires_approval: boolean;
  usage_count: number;
  last_used_on: string | null;
  last_used_by_task_id: string | null;
  tags: string[];
  environment: string;
  expires_on: string | null;
  rotation_interval_days: number;
  created_on: string;
  updated_on: string;
}

export interface SecretAccessAuditRecord {
  id: string;
  secret_id: string;
  user_id: string;
  access_type: string;
  task_id: string;
  tool_name: string;
  playbook_id: string;
  approved_by: string;
  approval_status: string;
  ip_address: string;
  execution_context: string;
  created_on: string;
}

export interface CreateSecretPayload {
  user_id: string;
  secret_name: string;
  secret_value: string;
  description?: string;
  category?: SecretCategory;
  risk_level?: SecretRiskLevel;
  requires_approval?: boolean;
  tags?: string[];
  environment?: string;
  expires_on?: string | null;
  rotation_interval_days?: number;
}

export interface UpdateSecretPayload {
  user_id: string;
  secret_name?: string;
  secret_value?: string;
  description?: string;
  category?: SecretCategory;
  risk_level?: SecretRiskLevel;
  requires_approval?: boolean;
  is_active?: boolean;
  tags?: string[];
  environment?: string;
  expires_on?: string | null;
  rotation_interval_days?: number;
}

export const listUserSecrets = (
  userId: string,
  params: {
    category?: string;
    risk_level?: string;
    is_active?: boolean;
    requires_approval?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  } = {},
) =>
  api.get<{ secrets: UserSecretSummary[]; total: number }>(
    `${SECRETS_SERVICE}/user-secrets`,
    { params: { user_id: userId, ...params } },
  );

export const getUserSecret = (secretId: string, userId: string) =>
  api.get<UserSecretSummary>(`${SECRETS_SERVICE}/user-secrets/${secretId}`, {
    params: { user_id: userId },
  });

export const createUserSecret = (payload: CreateSecretPayload) =>
  api.post<UserSecretSummary>(`${SECRETS_SERVICE}/user-secrets`, payload);

export const updateUserSecret = (secretId: string, payload: UpdateSecretPayload) =>
  api.put<UserSecretSummary>(`${SECRETS_SERVICE}/user-secrets/${secretId}`, payload);

export const deleteUserSecret = (secretId: string, userId: string) =>
  api.delete(`${SECRETS_SERVICE}/user-secrets/${secretId}`, {
    params: { user_id: userId },
  });

export const testSecretResolution = (userId: string, text: string) =>
  api.post<{
    masked_text: string;
    references: Array<{
      secret_name: string;
      found: boolean;
      masked_preview: string | null;
      risk_level: string | null;
      requires_approval: boolean | null;
      is_active: boolean | null;
    }>;
  }>(`${SECRETS_SERVICE}/user-secrets/test-resolution`, {
    user_id: userId,
    text,
  });

export const getSecretAuditHistory = (secretId: string, userId: string, limit = 100) =>
  api.get<{ audit: SecretAccessAuditRecord[] }>(
    `${SECRETS_SERVICE}/user-secrets/${secretId}/audit`,
    { params: { user_id: userId, limit } },
  );

export const listSecretNames = (userId: string) =>
  api.get<{
    names: Array<{
      secret_name: string;
      category: SecretCategory;
      masked_preview: string;
      risk_level: SecretRiskLevel;
    }>;
  }>(`${SECRETS_SERVICE}/user-secrets/names`, {
    params: { user_id: userId },
  });

// ── Projects ──────────────────────────────────────────────────────────────────

export interface ProjectSummary {
  id: string;
  name: string;
  description: string;
  owner_user_id: string;
  status: string;
  archived: boolean;
  metadata_json: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectChat {
  id: string;
  project_id: string;
  title: string;
  created_by_user_id: string;
  memory_enabled: boolean;
  summary: string;
  metadata_json: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectTimelineEvent {
  id: string;
  project_id: string;
  event_type: string;
  title: string;
  description: string;
  related_entity_type: string;
  related_entity_id: string;
  created_by: string;
  created_at: string;
}

export const getProjects = (params: {
  status?: string;
  include_archived?: boolean;
  limit?: number;
  offset?: number;
} = {}) => api.get<{ projects: ProjectSummary[]; total: number }>(`${TASK_SERVICE}/projects`, { params });

export const createProject = (data: { name: string; description?: string }) =>
  api.post<ProjectSummary>(`${TASK_SERVICE}/projects`, data);

export const getProject = (projectId: string) =>
  api.get<ProjectSummary>(`${TASK_SERVICE}/projects/${projectId}`);

export const updateProject = (projectId: string, data: {
  name?: string;
  description?: string;
  status?: string;
  archived?: boolean;
}) => api.put<ProjectSummary>(`${TASK_SERVICE}/projects/${projectId}`, data);

export const archiveProject = (projectId: string) =>
  api.delete(`${TASK_SERVICE}/projects/${projectId}`);

export const getProjectOverview = (projectId: string) =>
  api.get(`${TASK_SERVICE}/projects/${projectId}/overview`);

export const getProjectTasks = (projectId: string, params: {
  status?: string;
  limit?: number;
  offset?: number;
} = {}) => api.get(`${TASK_SERVICE}/projects/${projectId}/tasks`, { params });

export const getProjectChats = (projectId: string, params: {
  limit?: number;
  offset?: number;
} = {}) => api.get<{ chats: ProjectChat[]; project_id: string }>(
  `${TASK_SERVICE}/projects/${projectId}/chats`, { params },
);

export const createProjectChat = (projectId: string, data: {
  title: string;
  memory_enabled?: boolean;
}) => api.post<ProjectChat>(`${TASK_SERVICE}/projects/${projectId}/chats`, data);

export const getProjectArtifacts = (projectId: string, params: {
  limit?: number;
  offset?: number;
} = {}) => api.get(`${TASK_SERVICE}/projects/${projectId}/artifacts`, { params });

export const getProjectKnowledgeBases = (projectId: string, params: {
  limit?: number;
} = {}) => api.get(`${TASK_SERVICE}/projects/${projectId}/knowledge-bases`, { params });

export const getProjectTimeline = (projectId: string, params: {
  limit?: number;
  offset?: number;
} = {}) => api.get<{ events: ProjectTimelineEvent[]; project_id: string }>(
  `${TASK_SERVICE}/projects/${projectId}/timeline`, { params },
);

// ── Connectors ───────────────────────────────────────────────────────────────

export interface ConnectorType {
  type: string;
  name: string;
  description: string;
  auth_type: string;
  scopes: string[];
  icon: string;
}

export interface ConnectorInstance {
  id: string;
  connector_type: string;
  user_id: string;
  name: string;
  status: string; // pending | connected | error | revoked
  scopes: string[];
  config: Record<string, any>;
  last_synced_at: string;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export const listConnectorTypes = () =>
  api.get<{ connector_types: ConnectorType[] }>(`${CONNECTOR_SERVICE}/connectors/types`);

export const listConnectorInstances = (userId?: string) =>
  api.get<{ instances: ConnectorInstance[] }>(`${CONNECTOR_SERVICE}/connectors/instances`, {
    params: userId ? { user_id: userId } : {},
  });

export const createConnectorInstance = (data: {
  connector_type: string;
  name?: string;
  redirect_uri?: string;
}) => api.post<{ instance: ConnectorInstance; auth_url: string }>(
  `${CONNECTOR_SERVICE}/connectors/instances`,
  data,
);

export const createManualConnectorInstance = (data: {
  connector_type: string;
  name?: string;
  webhook_url?: string;
  access_token?: string;
}) => api.post<{ instance: ConnectorInstance; ok: boolean }>(
  `${CONNECTOR_SERVICE}/connectors/instances/manual`,
  data,
);

export const completeConnectorOAuth = (params: {
  connector_type?: string;
  code?: string;
  state: string;
  installation_id?: string;
  error?: string;
  error_description?: string;
  redirect_uri?: string;
}) => api.get<{ status: string; instance_id: string; connector_type: string }>(
  `${CONNECTOR_SERVICE}/connectors/oauth/callback`,
  { params },
);

export const getConnectorInstance = (instanceId: string) =>
  api.get<ConnectorInstance>(`${CONNECTOR_SERVICE}/connectors/instances/${instanceId}`);

export const deleteConnectorInstance = (instanceId: string) =>
  api.delete(`${CONNECTOR_SERVICE}/connectors/instances/${instanceId}`);

export const testConnectorInstance = (instanceId: string) =>
  api.get<{ status: string; ok: boolean }>(`${CONNECTOR_SERVICE}/connectors/instances/${instanceId}/status`);

export const refreshConnectorInstance = (instanceId: string) =>
  api.post<{ status: string }>(`${CONNECTOR_SERVICE}/connectors/instances/${instanceId}/refresh`);

export const listConnectorResources = (
  instanceId: string,
  resourceType = 'files',
  page = 1,
  perPage = 30,
) =>
  api.get<{ resources: any[] }>(`${CONNECTOR_SERVICE}/connectors/instances/${instanceId}/resources`, {
    params: { resource_type: resourceType, page, per_page: perPage },
  });

export const uploadFileToConnector = (
  instanceId: string,
  file: File,
  contextType: 'task' | 'chat',
  contextId: string,
  contextTitle: string,
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('context_type', contextType);
  form.append('context_id', contextId);
  form.append('context_title', contextTitle);
  return api.post<{ file: any }>(
    `${CONNECTOR_SERVICE}/connectors/instances/${instanceId}/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 },
  );
};

export const getGDriveStatus = (userId: string) =>
  api.get<{ connected: boolean; instance: ConnectorInstance | null }>(
    `${CONNECTOR_SERVICE}/connectors/gdrive/status`,
    { params: { user_id: userId } },
  );

export const getConnectorSyncLogs = (instanceId: string, limit = 50) =>
  api.get<{ logs: any[] }>(
    `${CONNECTOR_SERVICE}/connectors/instances/${instanceId}/logs`,
    { params: { limit } },
  );

// ── Agent Platform ────────────────────────────────────────────────────────────

const normalizeAgentServiceUrl = (value?: string): string => {
  const raw = (value || 'http://localhost:8001').replace(/\/+$/, '');
  return raw === '/api/desktop-service' ? '/api/agent-service' : raw;
};

const AGENT_SERVICE = normalizeAgentServiceUrl(process.env.REACT_APP_AGENT_SERVICE_URL);

export type AgentCategory =
  | 'SOFTWARE_ENGINEERING' | 'AGILE' | 'PROJECT_MANAGEMENT'
  | 'GOVERNMENT_CONTRACTING' | 'BUSINESS_DEVELOPMENT' | 'SALES'
  | 'MARKETING' | 'RESEARCH' | 'HR' | 'FINANCE' | 'DEVOPS' | 'OPERATIONS' | 'CUSTOM';

export type AgentVisibility = 'PRIVATE' | 'ORGANIZATION' | 'PUBLIC';

export type AgentType = 'KNOWLEDGE' | 'TASK' | 'WORKFLOW' | 'API';

export type AgentExecutionSource = 'UI' | 'API' | 'SCHEDULE' | 'WORKFLOW';

export interface ToolRecord {
  name: string;
  description: string;
  [key: string]: any;
}

export interface AgentRecord {
  id: string;
  name: string;
  author_id: string;
  agent_type: AgentType;
  playbook_id: string;
  description: string;
  short_description: string;
  icon: string;
  category: AgentCategory;
  published: boolean;
  featured: boolean;
  visibility: AgentVisibility;
  version: string;
  organization_id: string;
  knowledge_base_ids: string[];
  tool_ids: string[];
  connector_bindings: Record<string, string>;
  system_prompt: string;
  input_schema: Record<string, any>;
  output_schema: Record<string, any>;
  allow_scheduling: boolean;
  allow_api_execution: boolean;
  api_key_required: boolean;
  execution_count: number;
  subscriber_count: number;
  rating_average: number;
  rating_count: number;
  created_at: string;
  updated_at: string;
}

export interface AgentExecution {
  id: string;
  agent_id: string;
  task_id: string;
  status: string;
  started_at: string;
  completed_at: string;
  requested_by: string;
  execution_source: AgentExecutionSource;
  input_data: Record<string, any>;
  output_data: Record<string, any>;
}

export interface AgentScheduleRecord {
  id: string;
  agent_id: string;
  agent_name?: string;
  cron_expression: string;
  created_by: string;
  enabled: boolean;
  default_inputs_json: string;
  timezone: string;
  last_run_at: string;
  next_run_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AgentVersion {
  id: string;
  agent_id: string;
  version: string;
  playbook_version: string;
  change_log: string;
  published_at: string;
}

export interface CreateAgentPayload {
  name: string;
  agent_type?: AgentType;
  description?: string;
  short_description?: string;
  icon?: string;
  category?: AgentCategory;
  visibility?: AgentVisibility;
  playbook_id?: string;
  organization_id?: string;
  knowledge_base_ids?: string[];
  tool_ids?: string[];
  connector_bindings?: Record<string, string>;
  system_prompt?: string;
  input_schema?: Record<string, any>;
  output_schema?: Record<string, any>;
  allow_scheduling?: boolean;
  allow_api_execution?: boolean;
  api_key_required?: boolean;
}

export const createAgent = (payload: CreateAgentPayload) =>
  api.post<AgentRecord>(`${AGENT_SERVICE}/api/agents`, payload);

export const listAgents = (view: 'my' | 'shared' | 'subscribed' = 'my') =>
  api.get<{ agents: AgentRecord[] }>(`${AGENT_SERVICE}/api/agents`, { params: { view } });

export const getAgent = (agentId: string) =>
  api.get<AgentRecord>(`${AGENT_SERVICE}/api/agents/${agentId}`);

export const updateAgent = (agentId: string, payload: Partial<CreateAgentPayload>) =>
  api.put<AgentRecord>(`${AGENT_SERVICE}/api/agents/${agentId}`, payload);

export const deleteAgent = (agentId: string) =>
  api.delete<{ deleted: boolean }>(`${AGENT_SERVICE}/api/agents/${agentId}`);

export const publishAgent = (agentId: string, visibility: AgentVisibility = 'PUBLIC', changeLog = '') =>
  api.post<AgentRecord>(`${AGENT_SERVICE}/api/agents/${agentId}/publish`, {
    visibility,
    change_log: changeLog,
  });

export const unpublishAgent = (agentId: string) =>
  api.post<AgentRecord>(`${AGENT_SERVICE}/api/agents/${agentId}/unpublish`);

export const getAgentRating = (agentId: string) =>
  api.get<{ rating: number }>(`${AGENT_SERVICE}/api/agents/${agentId}/rating`);

export const rateAgent = (agentId: string, rating: number) =>
  api.post<{ rating: { rating: number }; rating_average: number; rating_count: number }>(
    `${AGENT_SERVICE}/api/agents/${agentId}/rating`,
    { rating },
  );

export const executeAgent = (
  agentId: string,
  inputs: Record<string, any> = {},
  executionSource: AgentExecutionSource = 'UI',
  knowledgeBaseIds?: string[],
) =>
  api.post<{ executionId: string; status: string; taskId: string }>(
    `${AGENT_SERVICE}/api/agents/${agentId}/execute`,
    { inputs, execution_source: executionSource, knowledge_base_ids: knowledgeBaseIds },
  );

export const getAgentExecution = (executionId: string) =>
  api.get<AgentExecution>(`${AGENT_SERVICE}/api/agents/executions/${executionId}`);

export const listAgentExecutions = (agentId: string, limit = 50) =>
  api.get<{ executions: AgentExecution[] }>(
    `${AGENT_SERVICE}/api/agents/${agentId}/executions`,
    { params: { limit } },
  );

export const subscribeAgent = (agentId: string) =>
  api.post(`${AGENT_SERVICE}/api/agents/${agentId}/subscribe`);

export const unsubscribeAgent = (agentId: string) =>
  api.delete(`${AGENT_SERVICE}/api/agents/${agentId}/subscribe`);

export const forkAgent = (agentId: string) =>
  api.post<AgentRecord>(`${AGENT_SERVICE}/api/agents/${agentId}/fork`);

export const listAgentVersions = (agentId: string) =>
  api.get<{ versions: AgentVersion[] }>(`${AGENT_SERVICE}/api/agents/${agentId}/versions`);

export const listTools = () =>
  api.get<{ tools: ToolRecord[] }>(`${AGENT_SERVICE}/api/tools`);

export const searchMarketplace = (params: {
  q?: string;
  category?: AgentCategory | '';
  agent_type?: AgentType | '';
  featured?: boolean;
  limit?: number;
}) =>
  api.get<{ agents: AgentRecord[] }>(`${AGENT_SERVICE}/api/agents/search`, { params });

export const createAgentSchedule = (
  agentId: string,
  cronExpression: string,
  defaultInputs: Record<string, any> = {},
  enabled = true,
  timezone = 'UTC',
) =>
  api.post<AgentScheduleRecord>(`${AGENT_SERVICE}/api/agents/${agentId}/schedules`, {
    cron_expression: cronExpression,
    default_inputs: defaultInputs,
    enabled,
    timezone,
  });

export const listAgentSchedules = (agentId: string) =>
  api.get<{ schedules: AgentScheduleRecord[] }>(`${AGENT_SERVICE}/api/agents/${agentId}/schedules`);

export const listAllAgentSchedules = () =>
  api.get<{ schedules: AgentScheduleRecord[] }>(`${AGENT_SERVICE}/api/agent-schedules`);

export const updateAgentSchedule = (
  agentId: string,
  scheduleId: string,
  payload: { cron_expression?: string; default_inputs?: Record<string, any>; enabled?: boolean; timezone?: string },
) =>
  api.put<AgentScheduleRecord>(
    `${AGENT_SERVICE}/api/agents/${agentId}/schedules/${scheduleId}`,
    payload,
  );

export const deleteAgentSchedule = (agentId: string, scheduleId: string) =>
  api.delete<{ deleted: boolean }>(
    `${AGENT_SERVICE}/api/agents/${agentId}/schedules/${scheduleId}`,
  );
